/* eslint-disable unicorn/prevent-abbreviations,unicorn/no-null */
import { computed, Directive, inject, input } from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";

import {
    FeatureFlagFacade,
    GRID_REMOVE_URL_FRAGMENT,
} from "@hermes/hermes-web-states-flipper-state";

import { isFilteredCategory } from "../helpers/editorial-cta.helper";

/**
 * Links to a filtered grid (without SEO facet) should not be followed by robots.
 * Add this directive to link anchor to add "nofollow" property.
 */
@Directive({
    selector: "a[hNoFollowOnFilteredGrid]",
    host: {
        "[attr.rel]": "rel()",
    },
})
export class NoFollowOnFilteredGridDirective {
    /**
     * Current link URL.
     * Used to determine whether 'nofollow' must be applied.
     */
    public readonly href = input<string | undefined>(undefined, {
        alias: "hNoFollowOnFilteredGrid",
    });

    /**
     * Optional base rel value coming from the parent.
     * 'nofollow' will be added or removed on top of this value.
     */
    public readonly baseRel = input<string>("");

    public readonly gridRemoveUrlFragment = toSignal(
        inject(FeatureFlagFacade).isActivated(GRID_REMOVE_URL_FRAGMENT),
        { initialValue: false },
    );

    /**
     * Final rel value applied to the host.
     * Preserves existing rel tokens and only adds or removes 'nofollow'.
     */
    protected readonly rel = computed(() => {
        if (!this.gridRemoveUrlFragment()) {
            return this.baseRel() || null;
        }

        const tokens = this.extractRelTokens(
            this.stripNoFollow(this.baseRel()),
        );

        if (this.hasGridQueryParam(this.href())) {
            tokens.add("nofollow");
        } else {
            tokens.delete("nofollow");
        }

        const value = [...tokens].join(" ").trim();

        return value || null;
    });

    private hasGridQueryParam(href: string | undefined): boolean {
        if (!href) {
            return false;
        }

        return isFilteredCategory(href);
    }

    private extractRelTokens(rel: string): Set<string> {
        return new Set(
            rel
                .split(/\s+/)
                .map((token) => token.trim())
                .filter(Boolean),
        );
    }

    private stripNoFollow(rel: string): string {
        return [...this.extractRelTokens(rel)]
            .filter((token) => token !== "nofollow")
            .join(" ");
    }
}
